import { makeAutoObservable } from "mobx";
import { RootStore } from "../../../stores";

interface AzureBlobStorage {
  id: string;
  fileName: string;
  originalName: string;
  containerName: string;
  blobUrl: string;
  contentType: string;
  fileSize: number;
  fileFormat: string;
  uploadedBy?: string;
  uploadedAt: string;
  metadata?: Record<string, any>;
  tags?: string[];
  isDeleted?: boolean;
  deletedAt?: string;
}

export class DrawingManagementStore {
  selectedMarkNumber: string | null = null;
  selectedDrawing: AzureBlobStorage | null = null;
  filterByType: string = "all";
  searchTerm: string = "";
  viewMode: "grid" | "list" = "grid";

  // Add our own loading state management
  isLoadingDrawings = false;
  errorLoadingDrawings: string | null = null;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  async setSelectedMarkNumber(markNumber: string) {
    this.selectedMarkNumber = markNumber;
  }

  setFilterByType(type: string) {
    this.filterByType = type;
  }

  setSearchTerm(term: string) {
    this.searchTerm = term;
  }

  setViewMode(mode: "grid" | "list") {
    this.viewMode = mode;
  }

  setSelectedDrawing(drawing: AzureBlobStorage | null) {
    this.selectedDrawing = drawing;
  }

  async loadDrawings(markNumber?: string) {
    const azureBlobStorageStore = (this.rootStore as any).azureBlobStorageStore;

    // Set loading state
    this.isLoadingDrawings = true;
    this.errorLoadingDrawings = null;

    const queryParams: any = {
      tags: markNumber ? [`mark-${markNumber}`] : ["drawing"],
      isDeleted: false,
      $limit: 100,
      $sort: {
        uploadedAt: -1,
      },
    };

    try {
      const foundDrawings = await azureBlobStorageStore.find({
        query: queryParams,
      });
      console.log("foundDrawings", foundDrawings);

      // Since enableStorage is now true, the entities should be automatically
      // populated in the store. Let's verify the count.
      console.log(
        "Total entities in store:",
        azureBlobStorageStore.entities?.length
      );

      // Clear loading state on success
      this.isLoadingDrawings = false;

      return foundDrawings;
    } catch (error) {
      console.error("Error loading drawings:", error);

      // Set error state
      this.isLoadingDrawings = false;
      this.errorLoadingDrawings =
        error instanceof Error ? error.message : "Failed to load drawings";

      throw error;
    }
  }

  async deleteDrawing(drawingId: string) {
    const azureBlobStorageStore = (this.rootStore as any).azureBlobStorageStore;

    try {
      await azureBlobStorageStore.patch(drawingId, {
        isDeleted: true,
      });

      if (this.selectedMarkNumber) {
        await this.loadDrawings(this.selectedMarkNumber);
      }
    } catch (error) {
      console.error("Error deleting drawing:", error);
      throw error;
    }
  }

  async uploadDrawing(file: File, metadata: any) {
    const { authStore } = this.rootStore;
    const azureBlobStorageStore = (this.rootStore as any).azureBlobStorageStore;

    try {
      // Step 1: Request a SAS URL from the backend
      const uploadData = {
        requestBlobUrl: true, // This triggers SAS URL generation
        fileName: file.name || "unknown.pdf",
        contentType: file.type || "application/octet-stream",
        fileSize: file.size,
        metadata: {
          ...metadata,
          uploadedBy: authStore.user?.email || "unknown",
        },
        tags: metadata.tags || [],
        // Don't send file data when requesting blob URL
      };

      console.log("Requesting SAS URL for file upload...");
      const fileRecord = await azureBlobStorageStore.create(uploadData);

      // Step 2: Upload directly to Azure if we got a SAS URL
      if (fileRecord.uploadUrl) {
        console.log("Got SAS URL, uploading directly to Azure...");

        try {
          const response = await fetch(fileRecord.uploadUrl, {
            method: "PUT",
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "Content-Type": file.type || "application/octet-stream",
              // Don't set Content-Length header - browser will handle it
            },
            body: file, // Send raw file, not Base64
          });

          if (!response.ok) {
            throw new Error(
              `Direct upload failed with status ${response.status}: ${response.statusText}`
            );
          }

          console.log("Upload successful, notifying backend...");

          // Step 3: Notify backend that upload is complete using PATCH
          await azureBlobStorageStore.patch(fileRecord.id, {
            uploadComplete: true,
            actualFileSize: file.size,
          });

          console.log("Upload completed successfully!");
        } catch (uploadError: any) {
          console.error("Upload failed:", uploadError);

          // Notify backend of failure using PATCH
          await azureBlobStorageStore.patch(fileRecord.id, {
            uploadComplete: false,
            uploadError: uploadError.message || "Upload failed",
          });

          throw uploadError;
        }
      } else {
        // Fallback: Use Base64 for smaller files if no SAS URL provided
        console.log("No SAS URL provided, using Base64 fallback...");

        const MAX_BASE64_SIZE = 10 * 1024 * 1024; // 10MB limit

        if (file.size > MAX_BASE64_SIZE) {
          throw new Error(
            `File size ${(file.size / 1024 / 1024).toFixed(
              2
            )}MB exceeds maximum upload size. Direct upload may not be configured properly.`
          );
        }

        // Convert to Base64 for fallback
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result as string;
            const base64Data = base64.split(",")[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
        });
        reader.readAsDataURL(file);
        const base64Data = await base64Promise;

        // Create with Base64 data
        await azureBlobStorageStore.create({
          ...uploadData,
          file: base64Data,
          requestBlobUrl: false, // Don't request blob URL for Base64 upload
        });
      }

      // Trigger a refresh to get the updated file in the list
      await this.loadDrawings(this.selectedMarkNumber || undefined);

      return fileRecord;
    } catch (error) {
      console.error("Error uploading drawing:", error);
      throw error;
    }
  }

  async downloadDrawing(drawing: AzureBlobStorage) {
    try {
      const response = await fetch(drawing.blobUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = drawing.originalName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading drawing:", error);
      throw error;
    }
  }

  get filteredDrawings() {
    const azureBlobStorageStore = (this.rootStore as any).azureBlobStorageStore;
    let filtered = azureBlobStorageStore.entities || [];

    // Filter out deleted items
    filtered = filtered.filter(
      (drawing: AzureBlobStorage) => !drawing.isDeleted
    );

    // Filter by selected mark number if set
    // NOTE: Only filter by mark number if the entity has tags, otherwise show all
    if (this.selectedMarkNumber) {
      filtered = filtered.filter((drawing: AzureBlobStorage) => {
        // If entity has tags, check for mark tag; otherwise, skip mark filtering
        if (drawing.tags && drawing.tags.length > 0) {
          return drawing.tags.includes(`mark-${this.selectedMarkNumber}`);
        }
        // If no tags, we'll show all drawings for now (could be legacy data)
        return true;
      });
    }

    if (this.filterByType !== "all") {
      filtered = filtered.filter(
        (drawing: AzureBlobStorage) =>
          drawing.metadata?.drawingType === this.filterByType
      );
    }

    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (drawing: AzureBlobStorage) =>
          drawing.originalName.toLowerCase().includes(search) ||
          drawing.fileName.toLowerCase().includes(search) ||
          drawing.metadata?.description?.toLowerCase().includes(search) ||
          drawing.metadata?.markNumber?.toLowerCase().includes(search)
      );
    }

    // Use slice() to create a copy before sorting to avoid mutating in-place
    return filtered
      .slice()
      .sort(
        (a: AzureBlobStorage, b: AzureBlobStorage) =>
          new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );
  }

  get drawingTypes() {
    const azureBlobStorageStore = (this.rootStore as any).azureBlobStorageStore;
    const types = new Set<string>();
    const drawings = azureBlobStorageStore.entities || [];

    drawings.forEach((drawing: AzureBlobStorage) => {
      if (drawing.metadata?.drawingType && !drawing.isDeleted) {
        types.add(drawing.metadata.drawingType);
      }
    });
    return Array.from(types);
  }

  get availableMarkNumbers() {
    return (this.rootStore as any).panelStore.entities
      .map((panel: any) => panel.markNumber)
      .filter(
        (mark: string, index: number, self: string[]) =>
          mark && self.indexOf(mark) === index
      )
      .slice() // Create a copy before sorting
      .sort();
  }

  get selectedPanel() {
    if (!this.selectedMarkNumber) return null;
    return (this.rootStore as any).panelStore.entities.find(
      (panel: any) => panel.markNumber === this.selectedMarkNumber
    );
  }
}
